/**
 * Project File Repository View
 *
 * 파일 보관함 메인 페이지.
 *
 * 레이아웃:
 *   ┌─────────────────────────────────────────────────────┐
 *   │  좌측 카테고리 레일  │  우측: 활성 카테고리 헤더    │
 *   │  - Map               │       + 업로드 zone           │
 *   │  - CAD               │       + 폴더/파일 목록        │
 *   │  - CSV               │                                │
 *   │  - ROI               │                                │
 *   │  - Learning          │                                │
 *   │  - Docs              │                                │
 *   │  - NVR               │                                │
 *   └─────────────────────────────────────────────────────┘
 *
 * 카테고리 식별자 (`map | cad | csv | roi | learning | docs | nvr`) 는 기존과 동일.
 * 디자인만 직관적으로 개선 — 카테고리별 색·아이콘·설명을 함께 노출.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  useTheme,
  alpha,
  Link,
} from '@mui/material';
import {
  Folder as FolderIcon,
  FilterList as FilterListIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { FileRepositoryViewModel } from '../viewmodels/FileRepositoryViewModel';
import { FileCategory, FileStatus, FileRepositoryState, FolderNode } from '../models/FileStorage';
import { FileListTable } from '../components/FileList/FileListTable';
import { FileMetadataEditModal } from '../components/FileList/FileMetadataEditModal';
import { Pagination } from '../components/FileList/Pagination';
import { FolderListView } from '../components/FolderView/FolderListView';
import { DedupFilterDialog } from '../components/FolderView/DedupFilterDialog';
import CategoryRail from './ProjectFileRepository/CategoryRail';
import CategoryHeader from './ProjectFileRepository/CategoryHeader';
import CompactUploadBar from './ProjectFileRepository/CompactUploadBar';
import MapEditorImportDialog from './ProjectFileRepository/MapEditorImportDialog';
import { getCategoryMeta } from './ProjectFileRepository/categoryMeta';

interface ProjectFileRepositoryViewProps {
  projectId: string;
  projectName: string;
}

export const ProjectFileRepositoryView: React.FC<ProjectFileRepositoryViewProps> = ({
  projectId,
  projectName: _projectName,
}) => {
  const { projectId: paramProjectId } = useParams<{ projectId: string }>();
  const currentProjectId = projectId || paramProjectId;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // State
  const [state, setState] = useState<FileRepositoryState>({
    currentCategory: 'map',
    files: [],
    folders: [],
    selectedFolder: null,
    currentPath: '',
    viewMode: 'files',
    loading: false,
    error: null,
    pagination: {
      page: 1,
      pageSize: 10,
      totalPages: 1,
      totalCount: 0,
    },
    uploadProgress: 0,
  });

  // Dedup filter dialog state
  const [dedupFilterOpen, setDedupFilterOpen] = useState(false);
  const [dedupFilterFolder, setDedupFilterFolder] = useState({ path: '', name: '' });

  const handleOpenDedupFilter = (folderPath: string, folderName: string) => {
    setDedupFilterFolder({ path: folderPath, name: folderName });
    setDedupFilterOpen(true);
  };

  const handleCloseDedupFilter = () => {
    setDedupFilterOpen(false);
  };

  // Metadata edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingFile, setEditingFile] = useState<{
    filename: string;
    description?: string;
    status?: FileStatus;
  } | null>(null);

  // ViewModel - stable instance, reads state via ref to avoid recreation on every state change
  const stateRef = useRef(state);
  stateRef.current = state;
  const viewModel = useMemo(
    () => new FileRepositoryViewModel(projectId, stateRef, setState),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId]
  );

  // Load files/folders on mount and category change
  useEffect(() => {
    if (state.currentCategory === 'learning') {
      viewModel.loadFolders();
    } else {
      viewModel.loadFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentCategory]);

  // Handlers
  const handleCategoryChange = (next: FileCategory) => {
    viewModel.setCategory(next);
  };

  const handleFilesSelected = async (files: File[]) => {
    await viewModel.uploadFiles(files);
  };

  // 맵 카테고리는 JSON 업로드 말고 맵 에디터에 등록된 맵을 그대로 가져올 수도 있다.
  const [mapImportOpen, setMapImportOpen] = useState(false);

  const handleDownload = (filename: string, originalName: string) => {
    viewModel.downloadFile(filename, originalName);
  };

  const handleDownloadLatest = (originalName: string) => {
    viewModel.downloadLatest(originalName);
  };

  // Wrapper for folder view - uses filename as both path and original name
  const handleFolderDownload = (filename: string) => {
    viewModel.downloadFile(filename, filename);
  };

  const handlePageChange = (page: number) => {
    viewModel.changePage(page);
  };

  const handleDelete = async (filename: string) => {
    if (window.confirm('이 파일을 삭제하시겠습니까?')) {
      await viewModel.deleteFile(filename);
    }
  };

  const handleBatchDelete = async (filenames: string[]) => {
    await viewModel.deleteFiles(filenames);
  };

  const handleBatchDownload = async (filenames: string[]) => {
    await viewModel.downloadMultiple(filenames);
  };

  const handleDownloadFolder = async (folderPath: string, folderName: string) => {
    await viewModel.downloadFolder(folderPath, folderName);
  };

  const handleDeleteFolder = async (folderPath: string) => {
    if (window.confirm(`폴더 "${folderPath}"와 그 안의 모든 내용을 삭제하시겠습니까?`)) {
      await viewModel.deleteFolder(folderPath);
    }
  };

  const handleDeleteFileFromFolder = async (filename: string) => {
    if (window.confirm(`파일 "${filename}"을(를) 삭제하시겠습니까?`)) {
      await viewModel.deleteFile(filename);
    }
  };

  const handleCloseError = () => {
    viewModel.clearError();
  };

  const handleFolderClick = (folder: FolderNode) => {
    viewModel.selectFolder(folder);
  };

  const handleFolderSelect = (folder: FolderNode) => {
    viewModel.selectFolderHighlight(folder);
  };

  const handleBackToFolders = () => {
    viewModel.backToFolders();
  };

  const handleEditMetadata = (filename: string, description?: string, status?: FileStatus) => {
    setEditingFile({ filename, description, status });
    setEditModalOpen(true);
  };

  const handleSaveMetadata = async (description: string, status: FileStatus) => {
    if (!editingFile) return;
    await viewModel.updateFileMetadata(editingFile.filename, description, status);
    setEditModalOpen(false);
    setEditingFile(null);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingFile(null);
  };

  /** 활성 카테고리 메타. */
  const activeMeta = useMemo(() => getCategoryMeta(state.currentCategory), [state.currentCategory]);

  /**
   * 카테고리 헤더에 표시할 통계.
   *
   * - 폴더 뷰(learning)에서는 폴더 노드의 size/count 를 합산.
   * - 파일 뷰에서는 pagination.totalCount + 현재 페이지 파일들의 size 합 (정확한 전체 합산은
   *   백엔드 응답에 없어서 페이지 단위 추정치만 노출).
   */
  const headerStats = useMemo(() => {
    if (viewModel.supportsFolderView && state.viewMode === 'folders') {
      const fileCount = state.folders.reduce((sum, f) => sum + (f.count ?? (f.isFolder ? 0 : 1)), 0);
      return { fileCount, totalBytes: null as number | null };
    }
    const totalBytes = state.files.reduce((sum, f) => sum + (f.size_bytes ?? 0), 0);
    return { fileCount: state.pagination.totalCount || state.files.length, totalBytes };
  }, [
    state.files,
    state.folders,
    state.viewMode,
    state.pagination.totalCount,
    viewModel.supportsFolderView,
  ]);

  return (
    <Box
      sx={{
        bgcolor: isDark ? '#0a0a0a' : '#f5f5f7',
        minHeight: 'calc(100vh - 64px)',
      }}
    >
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '260px minmax(0, 1fr)' },
          gap: { xs: 2, md: 2.5 },
          alignItems: 'flex-start',
        }}
      >
        {/* 좌측: 카테고리 레일 */}
        <CategoryRail active={state.currentCategory} onChange={handleCategoryChange} />

        {/* 우측: 헤더 + 업로드 + 컨텐츠 */}
        <Box sx={{ minWidth: 0 }}>
          <CategoryHeader
            meta={activeMeta}
            fileCount={headerStats.fileCount}
            totalBytes={headerStats.totalBytes}
            capabilityLabel={
              viewModel.isVersionedCategory
                ? '버전 관리 지원'
                : viewModel.supportsFolderView
                  ? 'CCTV별 폴더'
                  : undefined
            }
          />

          {/* 업로드 — 콤팩트 한 줄짜리 바 */}
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
            <CompactUploadBar
              category={state.currentCategory}
              onFilesSelected={handleFilesSelected}
              disabled={state.loading}
              uploading={state.loading && state.uploadProgress > 0}
              uploadProgress={state.uploadProgress}
              accent={activeMeta.accent}
            />
            </Box>
            {state.currentCategory === 'map' && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<MapIcon />}
                disabled={state.loading}
                onClick={() => setMapImportOpen(true)}
                sx={{ flexShrink: 0, textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                맵 에디터에서 가져오기
              </Button>
            )}
          </Box>

          <MapEditorImportDialog
            open={mapImportOpen}
            projectId={currentProjectId || ''}
            onClose={() => setMapImportOpen(false)}
            onImport={(file) => handleFilesSelected([file])}
          />

          {/* 컨텐츠: 폴더 뷰 또는 파일 뷰 */}
          <Box sx={{ position: 'relative' }}>
            {state.loading && !state.uploadProgress && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: 'rgba(0,0,0,0.1)',
                  zIndex: 10,
                  borderRadius: 2,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <Box
              sx={{
                opacity: state.loading ? 0.5 : 1,
                pointerEvents: state.loading ? 'none' : 'auto',
                borderRadius: 2,
                bgcolor: isDark ? alpha('#1a1a1a', 0.6) : alpha('#ffffff', 0.9),
                border: '1px solid',
                borderColor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.06),
                p: { xs: 1.5, md: 2 },
              }}
            >
              {/* 폴더 뷰 (learning/test) */}
              {viewModel.supportsFolderView && state.viewMode === 'folders' && (
                <Box>
                  {state.currentPath && (
                    <Box
                      sx={{
                        mb: 3,
                        p: 2,
                        borderRadius: 1,
                        bgcolor: isDark ? alpha('#fff', 0.05) : alpha('#000', 0.03),
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Link
                        component="button"
                        variant="body2"
                        onClick={handleBackToFolders}
                        sx={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          textDecoration: 'none',
                          '&:hover': { color: 'primary.main' },
                        }}
                      >
                        <FolderIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        상위 폴더로 돌아가기
                      </Link>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        현재 경로: /{state.currentPath}
                      </Typography>
                    </Box>
                  )}

                  {state.currentCategory === 'learning' && !state.currentPath && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FilterListIcon />}
                        onClick={() => handleOpenDedupFilter('', '전체 학습')}
                      >
                        전체 중복 필터
                      </Button>
                    </Box>
                  )}

                  <FolderListView
                    folders={state.folders}
                    onFolderClick={handleFolderClick}
                    onFolderSelect={handleFolderSelect}
                    selectedFolder={state.selectedFolder}
                    projectId={currentProjectId || ''}
                    category={state.currentCategory}
                    onDownload={handleFolderDownload}
                    onDeleteFolder={handleDeleteFolder}
                    onDeleteFile={handleDeleteFileFromFolder}
                    onDownloadFolder={handleDownloadFolder}
                    onDedupFilter={
                      state.currentCategory === 'learning' ? handleOpenDedupFilter : undefined
                    }
                  />
                </Box>
              )}

              {/* 파일 뷰 */}
              {state.viewMode === 'files' && (
                <Box>
                  {viewModel.supportsFolderView && state.selectedFolder && (
                    <Box
                      sx={{
                        mb: 3,
                        p: 2,
                        borderRadius: 1,
                        bgcolor: isDark ? alpha('#fff', 0.05) : alpha('#000', 0.03),
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Link
                        component="button"
                        variant="body2"
                        onClick={handleBackToFolders}
                        sx={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          textDecoration: 'none',
                          '&:hover': { color: 'primary.main' },
                        }}
                      >
                        <FolderIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        폴더 목록으로 돌아가기
                      </Link>
                      <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
                        {state.selectedFolder.name}
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      mb: 1.25,
                      pb: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: '1px solid',
                      borderColor: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.06),
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      파일 목록
                      <Typography
                        component="span"
                        sx={{ ml: 0.75, color: 'text.secondary', fontWeight: 500, fontSize: '0.8rem' }}
                      >
                        {state.pagination.totalCount}개
                      </Typography>
                    </Typography>
                  </Box>

                  <FileListTable
                    files={state.files}
                    category={state.currentCategory}
                    projectId={currentProjectId}
                    onDownload={handleDownload}
                    onDownloadLatest={handleDownloadLatest}
                    onDelete={handleDelete}
                    onBatchDelete={handleBatchDelete}
                    onBatchDownload={handleBatchDownload}
                    onEditMetadata={handleEditMetadata}
                  />

                  {state.pagination.totalPages > 1 && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      <Pagination
                        currentPage={state.pagination.page}
                        totalPages={state.pagination.totalPages}
                        onPageChange={handlePageChange}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={!!state.error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {state.error}
        </Alert>
      </Snackbar>

      {/* Metadata Edit Modal */}
      {editingFile && (
        <FileMetadataEditModal
          open={editModalOpen}
          filename={editingFile.filename}
          currentDescription={editingFile.description}
          currentStatus={editingFile.status}
          onClose={handleCloseEditModal}
          onSave={handleSaveMetadata}
        />
      )}

      {/* Dedup Filter Dialog */}
      <DedupFilterDialog
        open={dedupFilterOpen}
        onClose={handleCloseDedupFilter}
        projectId={currentProjectId || ''}
        folderPath={dedupFilterFolder.path}
        folderName={dedupFilterFolder.name}
        onComplete={() => {
          if (viewModel.supportsFolderView) {
            viewModel.loadFolders();
          }
        }}
      />
    </Box>
  );
};
